import React from 'react';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { AssetTreeView } from '../../../types/app/assetTreeView.types';
import { utcFormat } from '../../../types/core/date.types';
import * as commentApi from '../../../api/app/performanceCommentApi';
import { Loader } from '../../../components/core/Loader/Loader';
import { generateParentNames } from '../../../utils/core/functions/assetTreeView.fuctions';

import { Timezone } from '../../../components/app/TimezoneChanger/TimezoneChanger';
import { dateTimeShowFormat } from '../../../types/core/date.types';
import { Grid, Typography } from '@mui/material';
import { useTranslation } from '../../../contexts/translation';
import { useStyles } from './PerformanceCommentDashboard.styles';
import { AssetDashboardSetting } from '../../../types/app/asset.types';
import { PerformanceComment } from '../../../types/app/performanceComment.types';
import { DataGridTable } from '../../../components/core/DataGridTable/DataGridTable';
import { GridValueFormatterParams } from '@mui/x-data-grid';

interface Props {
  dateFrom: Moment;
  dateTo: Moment;
  assetTreeViewData: AssetTreeView;
  selectedAssets: AssetTreeView[];
  timezone: Timezone;
  granularity: string;
  settingData: AssetDashboardSetting;
}

export function DashboardPerformanceCommentLoader({
  dateFrom,
  dateTo,
  assetTreeViewData,
  selectedAssets,
  timezone,
  granularity,
}: Props) {
  const { t, lang } = useTranslation();
  const classes = useStyles();
  const [assetTreeViewDataCustom, setAssetTreeViewDataCustom] = useState<AssetTreeView | null>();
  const [comments, setComments] = useState<PerformanceComment[][] | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [refetch, setRefetch] = useState<boolean>(true);

  const columns = [
    {
      headerName: t(`page.dashboard.performance-comment.table-col-1`),
      field: 'assetName',
      width: 200,
      renderCell: (data: any) => {
        return (
          <span style={{ textOverflow: 'ellipsis', overflow: 'hidden' }} title={data.value}>
            {data.value}
          </span>
        );
      },
    },
    {
      headerName: t(`page.dashboard.performance-comment.table-col-2`),
      field: 'category',
      width: 200,
      renderCell: (data: any) => {
        return (
          <span style={{ textOverflow: 'ellipsis', overflow: 'hidden' }} title={data.value}>
            {data.value}
          </span>
        );
      },
    },
    {
      headerName: t(`page.dashboard.performance-comment.table-col-3`),
      field: 'comment',
      minWidth: 250,
      flex: 1,
      renderCell: (data: any) => {
        return <span title={data.value}>{data.value}</span>;
      },
    },
    { headerName: t(`page.dashboard.performance-comment.table-col-4`), field: 'qty', width: 100 },
    {
      headerName: t(`page.dashboard.performance-comment.table-col-5`),
      field: 'time',
      width: 200,
      valueFormatter: (params: GridValueFormatterParams) => {
        if (params.value)
          return moment.utc(params.value.toString()).tz(timezone.value).format(dateTimeShowFormat);
      },
    },
  ];

  // Rekurzivně doplníme parent names do stromu assetů
  useEffect(() => {
    if (!assetTreeViewData) return;
    setAssetTreeViewDataCustom(generateParentNames(assetTreeViewData, ''));
  }, [assetTreeViewData]);

  // Při změně filtru (assetu nebo datumu), se projde celé asset tree view, a pro každý asset se načtou datasety
  useEffect(() => {
    if (!selectedAssets || !dateFrom || !dateTo || !refetch) return;
    setLoading(true);

    const getCommentsForAssetTreeView = async () => {
      const resultsComments = await Promise.all(
        selectedAssets.map(async (asset) => {
          let comments = await commentApi.getListFiltered({
            assetId: asset.id,
            from: dateFrom.clone().format(utcFormat),
            to: dateTo.clone().format(utcFormat),
          });
          return comments;
        }),
      );
      setComments(resultsComments);
      setLoading(false);
    };
    getCommentsForAssetTreeView();
  }, [selectedAssets, dateFrom, dateTo, lang, refetch]);

  // Tabulkový pohled na komentáře
  const dashboardTableComments = useMemo(() => {
    if (isLoading) return <Loader />;

    let allComments: PerformanceComment[] = [];
    comments?.map((assetComments: any) => {
      assetComments.map((comment: PerformanceComment) => {
        allComments = [...allComments, comment];
      });
    });

    return (
      <DataGridTable
        data={allComments}
        columns={columns}
        fulltextSearch={true}
        exportOptions={{ fileName: 'performance_comments' }}
        autoRowHeight
      />
    );
  }, [comments, isLoading]);

  if (!assetTreeViewDataCustom || !comments) return <Loader />;

  if (selectedAssets.length === 0)
    return (
      <Typography variant={'h5'}>
        {t('page.dashboard.performance-comment.main-headline')}
      </Typography>
    );

  return (
    <Grid container>
      <Grid item xs={12} mt={2}>
        <Typography variant={'h5'} className={classes.datasetHeadline}>
          {t('page.dashboard.performance-comment.table-headline')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        {dashboardTableComments}
      </Grid>
    </Grid>
  );
}
